import { useMemo, useState } from "react";
import {
  selectIsPeerAudioEnabled,
  selectPeerMetadata,
  selectPeers,
  useHMSStore,
} from "@100mslive/react-sdk";
import { Hand, MicOff, Search } from "lucide-react";
import { PeerAvatar } from "@/components/common/PeerAvatar";
import { SidePaneHeader } from "@/components/meeting/SidePaneHeader";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { isInternalRole } from "@/lib/roles";

const EVERYONE = "everyone";
const RAISED_HAND = "raised-hand";

const ParticipantRow = ({ peer }) => {
  const metadata = useHMSStore(selectPeerMetadata(peer.id)) || {};
  const isAudioOn = useHMSStore(selectIsPeerAudioEnabled(peer.id));
  return (
    <li
      className="flex items-center gap-3 rounded-lg px-2 py-2 hover:bg-muted/60"
      data-testid={`participant_${peer.name}`}
    >
      <PeerAvatar name={peer.name} className="size-9 text-sm" />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium">
          {peer.name}
          {peer.isLocal ? (
            <span className="text-muted-foreground"> (you)</span>
          ) : null}
        </p>
        <p className="text-xs text-muted-foreground">{peer.roleName}</p>
      </div>
      {metadata.isHandRaised ? (
        <Hand className="size-4 text-amber-500" aria-label="Hand raised" />
      ) : null}
      {!isAudioOn ? (
        <MicOff className="size-4 text-muted-foreground" aria-label="Muted" />
      ) : null}
    </li>
  );
};

function hasHandRaised(peer) {
  try {
    return Boolean(JSON.parse(peer.metadata || "{}").isHandRaised);
  } catch {
    return false;
  }
}

export const ParticipantsPanel = ({ onClose }) => {
  const peers = useHMSStore(selectPeers).filter(
    peer => !isInternalRole(peer.roleName)
  );
  const [filter, setFilter] = useState(EVERYONE);
  const [search, setSearch] = useState("");
  const roles = useMemo(
    () => [...new Set(peers.map(peer => peer.roleName))],
    [peers]
  );

  const visible = peers.filter(peer => {
    if (filter === RAISED_HAND && !hasHandRaised(peer)) {
      return false;
    }
    if (
      filter !== EVERYONE &&
      filter !== RAISED_HAND &&
      peer.roleName !== filter
    ) {
      return false;
    }
    const query = search.trim().toLowerCase();
    return (
      !query ||
      peer.name.toLowerCase().includes(query) ||
      peer.roleName?.toLowerCase().includes(query)
    );
  });

  return (
    <div className="flex h-full flex-col">
      <SidePaneHeader
        closeLabel="Close participants"
        closeTestId="close_participants"
        onClose={onClose}
      />
      <div className="flex flex-col gap-2 border-b px-4 py-3">
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger
            className="w-full"
            data-testid="participant_list_filter"
            aria-label="Show"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={EVERYONE}>Everyone</SelectItem>
            <SelectItem value={RAISED_HAND}>Raised hand</SelectItem>
            {roles.map(role => (
              <SelectItem key={role} value={role}>
                {role}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="relative">
          <Search className="absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="pl-8"
            placeholder="Find a participant"
            value={search}
            onChange={event => setSearch(event.target.value)}
            onKeyDown={event => event.stopPropagation()}
          />
        </div>
      </div>
      <ScrollArea className="min-h-0 flex-1">
        {visible.length === 0 ? (
          <p className="px-4 py-6 text-center text-sm text-muted-foreground">
            {search || filter !== EVERYONE
              ? "No matching participants"
              : "No participants"}
          </p>
        ) : (
          <ul className="flex flex-col p-2">
            {visible.map(peer => (
              <ParticipantRow key={peer.id} peer={peer} />
            ))}
          </ul>
        )}
      </ScrollArea>
    </div>
  );
};
